/** Sign-in, sign-up and the staff console context (api/src/auth). */
import type { Principal, User } from './identity';
import type { Tenant } from './tenants';

/**
 * Successful login / challenge / refresh. There is no ID token: the identity
 * provider issues none, and the profile comes from GET /auth/me.
 */
export interface AuthTokens {
  /** The address the tokens were issued for; sent back on refresh. */
  username: string;
  /** Five minutes; the API renews it and returns the replacement as X-Access-Token. */
  accessToken: string;
  refreshToken?: string;
  expiresIn?: number;
  /** ISO 8601: when the session ends; nothing works past it. */
  sessionExpiresAt?: string | null;
}

/** POST /auth/mfa/totp/start: shown once for the authenticator app; the API never stores it. */
export interface TotpEnrolment {
  secret: string;
  /** otpauth://totp/Balsam%20Hub:<email>?secret=…&issuer=Balsam%20Hub */
  otpauthUri: string;
}

/** PUT /auth/mfa/preference. SMS needs a verified phone (409 PHONE_NOT_VERIFIED). */
export type MfaPreference = 'TOTP' | 'SMS' | 'NONE';

export const CHALLENGE_NAMES = [
  'NEW_PASSWORD_REQUIRED',
  'SMS_MFA',
  'SOFTWARE_TOKEN_MFA',
  'SMS_OTP',
] as const;
export type ChallengeName = (typeof CHALLENGE_NAMES)[number];

/** Login stopped at a second step; finish it with POST /auth/challenge. */
export interface AuthChallenge {
  challengeName: ChallengeName;
  /** Opaque, single use, ~3 minutes. */
  session: string;
  username: string;
  /** Masked delivery destination for SMS codes, e.g. +*******2671. */
  destination?: string;
}

export type AuthResult = AuthTokens | AuthChallenge;

export function isChallenge(result: AuthResult): result is AuthChallenge {
  return 'session' in result;
}

/** Where a sign-up code went (masked), from /auth/register and /auth/confirm/resend. */
export interface CodeDelivery {
  destination: string | null;
  medium: 'EMAIL' | 'SMS' | null;
}

/** POST /auth/register: the hub user and whether a confirmation code must be entered first. */
export interface RegisterResponse {
  user: User;
  confirmation: { required: boolean } & CodeDelivery;
}

/** Staff console context from /admin/auth/*. */
export interface AdminContext {
  adminType: 'global';
  accessibleTenants: 'all';
  selectedTenantId: string | null;
  selectedTenant: Tenant | null;
}

export interface AdminProfile {
  user: User;
  principal: Principal;
  context: AdminContext;
}

export interface ChallengeAnswer {
  username: string;
  challengeName: ChallengeName;
  session: string;
  code?: string;
  newPassword?: string;
}

export interface RegisterPayload {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
}

/** Every account-creating call carries these: the provider wants both names. The username is generated. */
export interface NewAccount {
  email: string;
  firstName: string;
  lastName: string;
}
